import PrismicImage from '@components/cms/PrismicImage';
import Heading from '@components/ui/Heading';
import cn from 'classnames';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import { ProductLineCarouselType } from '@adapters/prismic/types/plp';
import TagManager from 'react-gtm-module';
import { useLocale } from '@hooks/useLocale';
import styles from './ProductLineItem.module.scss';

interface ProductLineItemProps {
  item: ProductLineCarouselType['items'][0];
  index: number;
  isSelected: boolean;
  onSelect: (index: number, productLineName: string) => void;
  imageRatio?: string;
  carouselTitle?: string;
}

const ProductLineItem = ({
  item,
  index,
  isSelected,
  onSelect,
  imageRatio,
  carouselTitle,
}: ProductLineItemProps) => {
  const locale = useLocale();
  const { image, title, copy, product_line_name: productLineName } = item;

  const trackClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'user_interaction',
        event_name: 'click_franchise_carousel',
        interaction: {
          link_url: item?.product_line_name
            ? `${window.location.origin}${window.location.pathname}?facets.Collection=${productLineName}`
            : item?.link,
          link_text: title,
          carousel_slide_name: carouselTitle,
          carousel_type: 'Franchise',
          index: index,
        },
      },
    });
  };

  const handleClick = () => {
    onSelect(index, productLineName || '');
    trackClick();
  };

  const content = (
    <>
      <div
        className={cn(styles.imageContainer, {
          [styles.imageContainer34]: imageRatio === '3:4',
        })}
      >
        <PrismicImage
          src={image?.url ?? ''}
          alt={image?.alt ?? ''}
          layout="fill"
        />
      </div>
      <div className={styles.content}>
        {title && (
          <Heading
            semanticLevel="h2"
            styleLevel="5"
            className={cn(styles.heading, {
              [styles.checked]: isSelected,
            })}
          >
            {title}
          </Heading>
        )}
        {copy && (
          <MarkdownCopy text={copy?.[0]?.text ?? ''} className={styles.copy} />
        )}
      </div>
    </>
  );

  return (
    <article
      className={cn(styles.boxContainer, {
        [styles.checked]: isSelected,
      })}
      onClick={!item.link ? handleClick : undefined}
      role={!item.link ? 'radio' : undefined}
      aria-checked={!item.link ? isSelected : undefined}
      tabIndex={0}
      onKeyDown={
        !item.link
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
    >
      {!item.link ? (
        <>
          <input
            type="radio"
            checked={isSelected}
            onChange={handleClick}
            className={styles.hiddenCheckbox}
            aria-hidden="true"
          />
          {content}
        </>
      ) : (
        <a href={`/${locale}${item?.link}`} className={styles.link}>
          {content}
        </a>
      )}
    </article>
  );
};

export default ProductLineItem;
